/**
 * BL-1068 (ADR 034) — `evaluateLotFieldEditability`: the LOT field-editability
 * policy, the FIELD-axis projection of the same rule core `canBatchApply`
 * (change-axis) encodes. Answers, per field: *is it editable right now, and if
 * not, why?* — keyed on the ADR-023-derived lock classification plus context
 * (bids + caller permission).
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth enforcement,
 * no stamp writes — it only reads. Client-safe (nothing server-only transits),
 * safe inside `useMemo`. Sibling of `can-batch-apply.ts` / `derive-lot-phase.ts`.
 *
 * **Dual enforcement, single source.** The server `LotEditGateService` runs this
 * over the lock-read classification (authoritative → inline `fieldErrors`); the
 * client `ItemForm` runs the SAME function over the raw inputs it self-fetches
 * (advisory → per-field disable + hint). One function, no derived duplicate.
 *
 * **Scope.** Locks: `sold-lock` / `active-lock` (the sold and live bid-ladder
 * sets below), the BL-2352 pricing-basis lock (`has-bids` on `pricingType`) and
 * the `auctionId` move verdict; the rest of {@link LockReason}
 * is the shared vocabulary other evaluators produce. Catalog fields outside the
 * governed set are editable in EVERY phase (a sold lot's `make` still saves).
 *
 * **Cautions (BL-1908, ADR 034's `warn` tier).** The signed Lot Edit Rules Map
 * (PRJ-75, 2026-09-24 — `docs/artifacts/lot-edit-rules-map-09-24-26.html`) adds
 * a "warns first" tier: a field bidders can see gets a caution while the lot is
 * pre-bidding or live, and an Admin overriding a live lot's lock is told the lot
 * has bids. The rule book carries no invoice or settlement rules while the back
 * office is rebuilt (Michael Lawrie, 2026-09-25), so the map's one invoice-derived
 * cell — Item Location warning on a sold lot — is deliberately not implemented. A caution never blocks: every `warn` verdict is `editable: true`, so the
 * write gates (which act on `editable === false` only) are unaffected. Three
 * catalog fields — {@link LOT_CAUTION_FIELDS} — are cautioned but never locked.
 *
 * **Financially-dangerous edits are outside this policy by construction** —
 * hammer price, reverse-sale, dispose, swap stay on their guarded routes / the
 * invoice Price adjustment (`LotPriceAdjustmentService`,
 * hammer-price-sot.md), never in the field set below.
 */
import { deriveLotWindows, deriveTerminalDisposition, type LotWindowAuctionInput } from './derive-lot-phase';
import type { LotPhaseKind, LotTerminalInput } from './phase-tokens';
import { isLotFieldInEditScope, ROLE_SCOPE_HINT, type LotEditScope } from './lot-edit-scope';

/**
 * Fields locked once a lot is SOLD. Hammer price has its own guarded path
 * (the Adjust hammer window's invoice Price adjustment) and is intentionally
 * excluded. This is the
 * SINGLE definition — the server lot-write doors import these straight from this
 * core module (BL-1071 retired the admin-web `lot-lock-fields.ts` re-export
 * barrel; nothing re-declares the sets). Today that is the two single-lot routes
 * plus the dedicated reserve route. (This used to say "three doors … and, since
 * BL-1523, the batch sequencing PUT" — that door's field-lock adapter was since
 * retired along with the PUT it guarded; see the module doc on
 * `apps/admin-web/src/app/api/auctions/[id]/lots/route.ts`.)
 *
 * BL-1423 removed `lotNumber` (lifecycle consent: Michael Lawrie, 2026-08-26).
 * A lot number is a POSITION, and a position has exactly one writer now — the
 * reorder path (`PUT /lots/reorder`), which owns its own guard: the live-safe
 * interception, asked of the operator before the write. Governing it here as
 * well made every lot ahead of a sale unmovable with no workaround in the
 * product. It left `ACTIVE_LOCKED_FIELDS` in the same change because, at the
 * time, it had to: the evaluator iterated the SOLD set and only membership-tested
 * the ACTIVE one, so a member of ACTIVE that was not in SOLD was unreachable.
 * **That constraint is gone** — see {@link GOVERNED_LOCK_FIELDS}.
 *
 * BL-1308 removed `reserve` + `increment` (lifecycle consent: Michael Lawrie,
 * 2026-09-08) — and, with them, the grouping fields `parentLotId` / `linkType` /
 * `extensionGroupId`. Reserve and increment are **bidding-only**: no invoice
 * line, settlement line, or issued document reads either, so freezing them after
 * the hammer blocked harmless operator corrections for no benefit. They stay in
 * {@link ACTIVE_LOCKED_FIELDS} — the live bid-ladder lock is unchanged. The three
 * grouping fields left as a truth-fix, not a behaviour change: no route accepts
 * them in a body, so they were unreachable in this gate, while their presence
 * here forced `can-batch-apply.ts` to explain away a sold-row carve-out that only
 * looked like a contradiction.
 *
 * What remains is the money-bearing + document-stamping set: `startPrice` /
 * `taxStatus` / `taxRateId` / `sellerId` / `pricingType` / `acreage` feed invoice and
 * settlement math, and `ringId` stamps onto issued documents. Unlocking those
 * needs the resync mechanism BL-1307 covers.
 */
export const SOLD_LOCKED_FIELDS = [
  'ringId',
  'startPrice',
  // BL-1767: the lot's tax treatment replaced the `isTaxable` flag — both halves
  // (whether it is taxed, and at which rate) are invoice inputs.
  'taxStatus',
  'taxRateId',
  'sellerId',
  // BUG-1131: the per-acre/total pricing basis and the acreage that multiplies
  // it are billing inputs (a per-acre lot bills `startPrice × acreage`), so they
  // lock with the same bid-ladder fields — never re-price a sold lot's basis.
  // An edit to either re-derives the hammer (ADR 061 — Derived Hammer § 3.3,
  // BUG-2032), so this sold lock is what keeps a sold price from moving.
  // BL-2352 corrected BL-1523's claim that these locks keep the bidder's live
  // whole-tract totals single-source: they never covered a pre-bidding or unbid
  // lot, and an Admin overrides acreage on a live one. BL-2359 made the bidder
  // side single-source by construction instead: both fields ride the lot
  // snapshot (ADR 035 — Money Units § 6), so no bidder surface depends on them
  // being locked. The sold lock above is still load-bearing for the hammer.
  'pricingType',
  'acreage',
] as const;

/**
 * Fields locked once a lot is ACTIVE (live bidding, with a placed bid) — the
 * bid-ladder set. Unchanged by BL-1308: `reserve` + `increment` are still locked
 * mid-sale and still clearable by `AUCTIONS_MANAGE`. (BL-1908 deleted the
 * clerk-only override of `increment` / `reserve`: BL-1446 retired the Clerk role,
 * and only Admins — who hold `AUCTIONS_MANAGE` — hold `clerking.manage` now.)
 *
 * **This is NOT a subset of `SOLD_LOCKED_FIELDS`, and hasn't been since BL-1308**
 * — `reserve` and `increment` are active-locked but sold-editable. The old subset
 * invariant was load-bearing only because the evaluator looped the sold set;
 * {@link GOVERNED_LOCK_FIELDS} removed that coupling, and no code may reintroduce
 * it. A loop over `SOLD_LOCKED_FIELDS` today silently skips both fields, which
 * makes them editable during live bidding while every route still returns 200.
 */
export const ACTIVE_LOCKED_FIELDS = [
  'ringId',
  'startPrice',
  'reserve',
  'increment',
  'sellerId',
  'pricingType',
  'acreage',
] as const;

/**
 * **The single iteration source for the field-editability policy** — the UNION of
 * {@link SOLD_LOCKED_FIELDS} and {@link ACTIVE_LOCKED_FIELDS}, i.e. every field
 * this module can return a lock verdict for.
 *
 * Introduced by BL-1308 because the two lock sets stopped nesting. Before it,
 * `SOLD_LOCKED_FIELDS` did double duty — the sold-lock set AND "everything the
 * policy governs" — which worked only while sold happened to be a superset. Three
 * places relied on that coincidence (this evaluator and both single-lot PATCH
 * doors), each with a comment asserting it, and each would have failed **silently**
 * once it broke: a field missing from the iteration source is never handed to the
 * gate, so its lock evaporates and the write lands with a 200.
 *
 * **Iterate this. Membership-test the other two.** If you find yourself writing
 * `for (const f of SOLD_LOCKED_FIELDS)` or `SOLD_LOCKED_FIELDS.filter(...)`, you
 * are reintroducing that bug.
 *
 * Order is sold-set-first then the active-only remainder, so the verdict map's key
 * order is stable for snapshot-style assertions.
 */
export const GOVERNED_LOCK_FIELDS = [
  ...SOLD_LOCKED_FIELDS,
  ...ACTIVE_LOCKED_FIELDS.filter(
    (f) => !(SOLD_LOCKED_FIELDS as readonly string[]).includes(f),
  ),
] as const as readonly (
  | (typeof SOLD_LOCKED_FIELDS)[number]
  | (typeof ACTIVE_LOCKED_FIELDS)[number]
)[];

/**
 * BL-1908 — the catalog fields the rule book CAUTIONS on but never locks: the
 * signed map warns while bidders can see the lot (Item Location also on a sold
 * lot), and none of them feeds an invoice total or a bid ladder, so no phase
 * locks them. Keys are the lot form's field names. Deliberately NOT part of
 * {@link GOVERNED_LOCK_FIELDS}: the write gates iterate that set to decide
 * refusals, and a caution never refuses.
 */
export const LOT_CAUTION_FIELDS = ['categoryId', 'locationId', 'isTitledItem'] as const;

/** A caution-only field — see {@link LOT_CAUTION_FIELDS}. */
export type LotCautionFieldKey = (typeof LOT_CAUTION_FIELDS)[number];

/** A lot field the editability policy can govern (the union of the lock sets —
 *  money-bearing + bid-ladder). Fields NOT in this union (pure catalog: `name`,
 *  `description`, `make`, …) are always editable and simply have no verdict.
 *
 *  BL-1308: keyed off {@link GOVERNED_LOCK_FIELDS} rather than the sold set,
 *  which stopped being the union when `reserve`/`increment` became sold-editable. */
export type LotFieldKey =
  | (typeof SOLD_LOCKED_FIELDS)[number]
  | (typeof ACTIVE_LOCKED_FIELDS)[number];

/** The full governed key set the evaluator returns a verdict for: the
 *  {@link GOVERNED_LOCK_FIELDS} union PLUS `auctionId` (BL-1070 — its own
 *  phase+bid predicate, not a sold/active lock).
 *
 *  Kept distinct from {@link LotFieldKey} because `auctionId` is decided by a
 *  separate rule ({@link evaluateLotMoveVerdict}) rather than by sold/active lock
 *  membership — so it is additive to the verdict map without belonging to either
 *  lock set. Callers iterating the lock fields use {@link GOVERNED_LOCK_FIELDS};
 *  this type is for indexing the returned map. */
export type LotGovernedFieldKey = LotFieldKey | 'auctionId';

/**
 * Why a field is locked. The lot evaluator produces `sold-lock` / `active-lock`
 * this card, and `has-bids` for the BL-2352 pricing-basis lock and the move rule; the remainder are ADR 034's shared vocabulary, produced by the
 * auction evaluator and the converged core (BL-1071 / BL-1072).
 */
export type LockReason =
  | 'terminal'
  | 'sold-lock'
  | 'active-lock'
  | 'bid-revert'
  | 'ceiling'
  | 'group-locked'
  | 'has-bids'
  // BUG-1528 DELETED `'schedule-locked'`. BL-1571 added it for the auction
  // SCHEDULE freeze — deliberately not `phase-locked`, since it keyed on
  // `isLiveSafe(lots)` rather than the phase — and BUG-1528 changed that tier's
  // verdict from `blocked` to `warn`, leaving the reason with zero producers. A
  // `LockReason` no evaluator can emit is a shape a reader will design against
  // (ADR 044 — Must Consolidate: the alternative has to be unreachable, not just
  // unused). The freeze's own `CloseTimesResult['skipped']` value of the same
  // name is unrelated and still live.
  | 'phase-locked'
  // BL-1687 — the AUCTION evaluator's format refusal: `type` is fixed at
  // creation and refused in every phase after (ADR 034 matrix signed
  // 2026-09-11, D7). Every other auction phase lock became a caution in the same
  // item; BL-2098 (Michael Lawrie, 2026-10-01) brought back exactly one,
  // `phase-locked` on an ended sale's `timeZone`. The LOT evaluator also emits
  // `phase-locked` (the `auctionId` move verdict).
  | 'immutable'
  | 'catalog-published'
  // BL-1666 — the CUSTOMER evaluator's only reason: the buyer proved this
  // contact channel on their own login, so staff can read it but not change it.
  // Not `identity-locked`: what locks the field is the VERIFICATION, not the
  // existence of a login (an unverified sign-up leaves the field editable).
  | 'identity-verified'
  // BL-2318 — the caller's ROLE may not edit this field at all (Accounting edits
  // tax, load-out fee and title paperwork only; see `lot-edit-scope.ts`). Not a
  // phase lock: it holds in every phase, and a sold lock outranks it.
  | 'role-scope';

/**
 * Per-field verdict (ADR 034's three tiers). Both evaluators produce `warn`: the
 * auction's cautions (BL-1687) and, since BL-1908, the lot's (the signed rules
 * map's "warns first" cells). A `warn` verdict is always editable.
 */
export type FieldVerdict =
  | { editable: true }
  | { editable: true; warn: string }
  | { editable: false; reason: LockReason; hint: string };

/** {@link FieldVerdict} narrowed to the caution-only fields — never a lock. */
export type CautionFieldVerdict = Exclude<FieldVerdict, { editable: false }>;

/**
 * The lock half of a verdict — what a control needs to disable itself and say why.
 *
 * @param verdict A field's verdict, or null/undefined when the rule book has none.
 * @returns The locked verdict, or null when the field is editable.
 * @sideEffect none — pure.
 */
export function fieldLock(
  verdict: FieldVerdict | null | undefined,
): Extract<FieldVerdict, { editable: false }> | null {
  return verdict != null && verdict.editable === false ? verdict : null;
}

/**
 * The caution half of a verdict (BL-1908) — the sentence an editable field shows
 * before saving.
 *
 * @param verdict A field's verdict, or null/undefined when the rule book has none.
 * @returns The caution, or undefined when there is none (or the field is locked).
 * @sideEffect none — pure.
 */
export function fieldCaution(verdict: FieldVerdict | null | undefined): string | undefined {
  return verdict != null && verdict.editable && 'warn' in verdict ? verdict.warn : undefined;
}

/** The lot evaluator's whole answer: every governed field, plus the caution-only ones. */
export type LotFieldVerdicts = Record<LotGovernedFieldKey, FieldVerdict> &
  Record<LotCautionFieldKey, CautionFieldVerdict>;

/** Operator-facing lock hints (ride `<FormField>`'s helperText slot). */
export const SOLD_LOCK_HINT = "This lot is sold — this field can't be changed.";
export const ACTIVE_LOCK_HINT = 'This lot has live bids — this field is locked.';
/**
 * BL-2352 — the pricing-basis lock's hint. Copy approved by Michael Lawrie,
 * 2026-10-08.
 */
export const PRICING_BASIS_LOCK_HINT =
  'This lot has bids, so its pricing basis is locked — changing it would change every bid.';

/**
 * BL-1904 — the two tax fields name where a sold lot's tax IS changed: its
 * invoice. The lock itself is unchanged (both stay in {@link SOLD_LOCKED_FIELDS});
 * later invoicing work writes an invoice-side tax change back to the lot (Dan
 * Saper, 2026-09-25). Every sold-locked field not listed reads
 * {@link SOLD_LOCK_HINT}.
 */
const SOLD_TAX_LOCK_HINT = 'This lot is sold — change its tax on the invoice.';
const SOLD_LOCK_HINT_BY_FIELD: Partial<Record<LotGovernedFieldKey, string>> = {
  taxStatus: SOLD_TAX_LOCK_HINT,
  taxRateId: SOLD_TAX_LOCK_HINT,
};
/**
 * BL-1523 — the ROOT-level copy each lock rejection carries alongside its
 * per-field `fieldErrors`. Defined here, beside the per-field hints, because
 * three doors now reject a locked-field edit — the two single-lot routes and the
 * batch sequencing PUT — and "the batch route returns the same locked-field error
 * the single-lot PATCH returns" has to be a property of the code, not a claim in
 * a ticket. The `error` string is only the fallback a client shows when no field
 * in `fieldErrors` matches a rendered input (ADR 016).
 */
export const SOLD_LOCK_ERROR = 'Some fields can’t be edited on a sold lot.';
export const ACTIVE_LOCK_ERROR = 'Some fields can’t be edited while this lot has live bids.';

/** BL-1070 — the `auctionId` (move/assign/reassign) lock hint. Verbatim on both
 *  the disabled menu-item tooltip (client) and the inline `fieldErrors.auctionId`
 *  server rejection, so the two enforcement sides read identically. BL-1870: the
 *  same verdict gates "Remove from auction" (a move with no destination).
 *  BUG-1878 (Dan Saper, 2026-09-29): one line, so it no longer buries the lot
 *  ⋯ menu. "Bids" covers pre-bids, the item already names the action, and
 *  Relist sits right below it in the same menu. */
export const MOVE_LOCK_HINT = 'Only Draft or Upcoming lots with no bids.';

/** BUG-1844 — the inline `fieldErrors.auctionId` a move into the lot's OWN auction
 *  returns. A same-auction "move" would run the move writer, which clears the
 *  lot's ring and number and renumbers the auction, so both move doors refuse it. */
export const MOVE_SAME_AUCTION_HINT = 'This lot is already in that auction.';

/** BUG-1845 — the refusal when the target auction already holds a live lot of the
 *  same inventory item (the unique active-item-per-auction index). One sentence for
 *  every door that lands an item in an auction: create / place, and move. */
export const ITEM_ALREADY_IN_AUCTION_HINT = 'This item is already in this auction';

/** The two ISO 4217 codes a currency refusal names: the lot's and the target auction's. */
export interface LotAuctionCurrencies {
  lotCurrency: string;
  auctionCurrency: string;
}

/**
 * BL-2286 — the single-lot refusal (`fieldErrors.auctionId`) when a lot priced in
 * one currency is moved, assigned or relisted into an auction that sells in
 * another. Never re-stamped: the lot's prices are in its own currency (ADR 060 —
 * Money Type § 6). Copy approved by Michael Lawrie, 2026-10-07.
 *
 * @param c - The lot's and the target auction's currency codes.
 * @returns The inline sentence.
 */
export function lotCurrencyMismatchHint(c: LotAuctionCurrencies): string {
  return `This lot is priced in ${c.lotCurrency}. That auction sells in ${c.auctionCurrency}.`;
}

/**
 * BL-2286 — the same refusal as one item's reason in a placement batch (Add
 * Items, bulk Assign to auction), worded like its sibling blocker reasons.
 *
 * @param c - The lot's and the target auction's currency codes.
 * @returns The per-item reason.
 */
export function lotCurrencyMismatchBlockerReason(c: LotAuctionCurrencies): string {
  return `Priced in ${c.lotCurrency}; this auction sells in ${c.auctionCurrency}`;
}

/**
 * BL-2286 — the bulk Move refusal: some selected lot is priced in another
 * currency than the target auction, so nothing moved.
 *
 * @param auctionCurrency - The target auction's currency code.
 * @returns The toast sentence.
 */
export function bulkMoveCurrencyMismatchError(auctionCurrency: string): string {
  return `One or more selected lots are priced in a different currency than that auction (${auctionCurrency}).`;
}

/**
 * BL-1908 — the rule book's cautions (the signed rules map's `warn` cells and the
 * Admin override). Operator-facing: they ride the lot panel's helper-text slot
 * under the field, and bulk review steps list them per lot.
 */
export const LOT_BIDDERS_SEE_IT_WARN = 'Bidders see this change right away.';
/**
 * Ring on a pre-bidding lot. The schedule is not frozen until a lot goes live
 * (`isLiveSafe` — pre-bidding is deliberately outside it), so a ring move re-deals
 * the auction's lot numbers and re-derives close times from the ring schedule.
 */
export const LOT_RING_PRE_BIDDING_WARN =
  'Bidders see this change right away — moving rings can change lot numbers and when this lot closes.';
/**
 * Ring on a live lot. The auction is live-safe, so no close time is recomputed; the
 * moved lot takes the next number at the end of its new ring and no other lot's
 * number moves (BUG-1523's placement).
 */
export const LOT_RING_LIVE_WARN =
  'Bidders see this change right away — this lot gets a new number at the end of the ring; its close time stays the same.';
/**
 * Starting Price on a pre-bidding lot with a standing pre-bid. At open only max bids
 * at or above the floor (`GREATEST(start_price, first increment)`) resolve into the
 * opening bid (`LotOpeningService`), so a pre-bid under a raised price drops out.
 */
export const LOT_PRE_BID_START_PRICE_WARN =
  'This lot has pre-bids — a pre-bid below the new starting price won’t count when bidding opens.';
/** Starting Price on a live lot with no bids yet: it is the minimum first bid (`nextMinimumBid`). */
export const LOT_LIVE_START_PRICE_WARN =
  'Bidders see this change right away — it’s the lowest bid they can place.';
/**
 * Starting Price, an Admin overriding a live lot with bids. Once a bid exists every
 * next bid is the current bid plus the increment, so the starting price no longer
 * moves anything bidders do — the generic has-bids line would overstate it.
 */
export const LOT_BIDDING_STARTED_START_PRICE_WARN =
  'Bidding has started — the starting price no longer affects bids.';
/** An Admin clearing a live lot's bid-ladder lock (the map's "an Admin can override" cells). */
export const LOT_ACTIVE_OVERRIDE_WARN =
  'This lot has bids — as an Admin you can still change this, and bidders see it right away.';

/** The Admin override's caution where a field says something more specific than {@link LOT_ACTIVE_OVERRIDE_WARN}. */
const OVERRIDE_WARN_BY_FIELD: Partial<Record<LotGovernedFieldKey, string>> = {
  startPrice: LOT_BIDDING_STARTED_START_PRICE_WARN,
};

/**
 * The derived phases in which bidders can see the lot — the map's "Accepting
 * pre-bids" and "Live" columns (Live = accepting bids, last call, awaiting
 * result and paused). Keyed on the AUCTION-AWARE phase (ADR 023): the lot's own
 * stamps would still read "pre-bidding" after its auction was moved back to
 * Upcoming (BUG-1772).
 */
const BIDDER_VISIBLE_PHASES: ReadonlySet<LotPhaseKind> = new Set<LotPhaseKind>([
  'pre-bidding',
  'open-for-bidding',
  'last-call',
  'awaiting-resolution',
  'paused',
]);

/** The decision-stamp + window inputs the lock classification reads — the
 *  terminal-stamp subset, the lot's window/pause stamps, and its auction's two
 *  window stamps.
 *
 *  BL-1070 added `preBiddingAt` + `pausedAt` so `isDraftOrReady` (the movable
 *  window) can exclude the pre-bidding and paused phases. BUG-1772 added
 *  `auction`: both windows are two-stamp (auction then lot, ADR 023), and a lot
 *  keeps its own stamps when its auction is moved back a phase. Required, so tsc
 *  names every caller; `null` is a lot in no auction. */
export type LotEditLockStateInput = LotTerminalInput & {
  biddingOpenedAt: string | null;
  preBiddingAt: string | null;
  pausedAt: string | null;
  auction: LotWindowAuctionInput | null;
};

/**
 * Derive the two lock-relevant classifications the field policy keys on, with
 * the EXACT semantics of the server lock read
 * (`LotRepository.lockLotForEditWithBidPresence`, BL-726/BL-1025):
 *
 * - `isSold`   — the lot is hammered (terminal disposition `sold`).
 * - `isActive` — the lot's bidding window is open (its auction has opened AND
 *   the lot has) AND the lot is not terminal. Deliberately NOT
 *   `isLotPhaseActive`, which excludes paused/ready: a paused lot, or one past
 *   its close awaiting a result, keeps the live bid-ladder lock. BUG-1772: the
 *   auction's stamp is read too, so an auction moved back from Accepting bids
 *   releases its lots' live locks along with their phase.
 *
 * The single source both the repo lock read and the client detail DTO call, so
 * the two enforcement sides can't drift.
 *
 * - `isDraftOrReady` (BL-1070) — the lot is in the movable window: NOT terminal,
 *   NOT paused, and neither bidding window is open. The negation of every phase
 *   past `ready` on the ADR-023 ladder, over the same window read
 *   (`deriveLotWindows`) `deriveLotPhase` uses — BUG-1772: an auction moved back
 *   to Upcoming puts its lots back in the window, as their phase already shows.
 *   (No `now`: a lot whose window is open but whose close time is unset derives
 *   `ready` yet stays out of the window here — the conservative side.)
 *
 * - `isSchedulable` (BUG-1597) — the lot has not SETTLED, so its close time is
 *   still the operator's to set. Simply `!isTerminal`, which makes it a strict
 *   SUPERSET of `isActive`: it additionally admits draft, ready and pre-bidding.
 *
 *   It exists because the close-time editor was borrowing `isActive`, and the
 *   two answer different questions. `isActive` asks "is bidding running", which
 *   is right for the bid-ladder locks it feeds — changing a reserve under a live
 *   bidder is the thing those locks stop. Nothing about *scheduling* needs
 *   bidding to have opened; a lot that has not started is precisely a lot whose
 *   clock is still free to move.
 *
 *   Why the difference is load-bearing rather than pedantic: on a live-safe
 *   auction the automatic recompute and all three bulk schedule editors are
 *   frozen (ADR 036 — Mutation Cascades §5), and `closeTime` is not a governed
 *   field in this rule book, so `ChangeCloseTimeService` is the ONLY editor
 *   left. Gating it on `isActive` meant a lot reopened to `ready` mid-sale had
 *   no reachable way to change its close time at all — see BUG-1597.
 *
 *   Terminality alone is the whole rule. "Does it have a close time to move" is
 *   a separate precondition and stays where it already lives, on
 *   `ChangeCloseTimeService`'s `NO_CLOSE_TIME` branch.
 *
 * @sideEffect none — pure.
 */
export function deriveLotEditLockState(input: LotEditLockStateInput): {
  isSold: boolean;
  isActive: boolean;
  isDraftOrReady: boolean;
  isSchedulable: boolean;
} {
  const terminal = deriveTerminalDisposition(input);
  const isTerminal = terminal != null;
  const windows = deriveLotWindows(input, input.auction);
  return {
    isSold: terminal?.kind === 'sold',
    isActive: windows.biddingOpen && !isTerminal,
    isDraftOrReady:
      !isTerminal && input.pausedAt == null && !windows.biddingOpen && !windows.preBiddingOpen,
    isSchedulable: !isTerminal,
  };
}

/**
 * BL-1073 — the two {@link LockReason} values the `auctionId` move rule can
 * actually produce. Narrower than the full union on purpose: the batch engine
 * maps a blocked move verdict straight onto a `BatchBlockReason`, and that
 * mapping is only total because this is exactly two values. Widen it and the
 * batch adapter fails to compile — which is the intended alarm, not an
 * inconvenience.
 */
export type MoveLockReason = Extract<LockReason, 'has-bids' | 'phase-locked'>;

/** {@link FieldVerdict} narrowed to the move rule's reason set. Assignable to
 *  `FieldVerdict` wherever the general shape is expected. */
export type MoveFieldVerdict =
  | { editable: true }
  | { editable: false; reason: MoveLockReason; hint: string };

/**
 * BL-1073 (ADR 034) — the `auctionId` (move / assign / reassign) rule, extracted
 * as a named predicate so the SINGLE-lot move gate (`LotEditGateService`'s
 * `runGatedLotMove`, which calls it directly) and the BULK
 * `move-to-auction` batch action (`verdictForBatch`) consult ONE rule body
 * rather than two copies. That shared identity is what makes "a lot you can't
 * move singly is equally un-movable, and equally reasoned, in bulk" a property
 * of the code instead of a claim.
 *
 * Editable ⟺ draft/ready AND no bids AND no active pre-bids. **Phase outranks
 * the bid gate:** a non-draft/ready lot is `phase-locked` regardless of bids; a
 * draft/ready lot with bids/pre-bids is `has-bids`. `AUCTIONS_MANAGE` does NOT
 * override either (unlike the bid-ladder lock) — moving bid state between
 * auctions is never permitted, which is why this predicate takes no permission
 * input at all.
 *
 * @param input.isDraftOrReady   The lot is in the movable window (see {@link deriveLotEditLockState}).
 * @param input.hasBids          The lot has ≥1 bid. The server's move gate passes the strict read (any
 *   bid row, withdrawn included — BL-1908 keeps it strict while the field locks count placed bids only).
 * @param input.hasActivePreBids The lot has ≥1 active `max_bids` (standing pre-bid). Callers whose
 *   `hasBids` already folds pre-bids in (the batch row's UNION read) pass `false` here.
 * @returns the `auctionId` {@link FieldVerdict}.
 * @sideEffect none — pure.
 */
export function evaluateLotMoveVerdict(input: {
  isDraftOrReady: boolean;
  hasBids: boolean;
  hasActivePreBids: boolean;
}): MoveFieldVerdict {
  const { isDraftOrReady, hasBids, hasActivePreBids } = input;
  if (!isDraftOrReady) return { editable: false, reason: 'phase-locked', hint: MOVE_LOCK_HINT };
  if (hasBids || hasActivePreBids) {
    return { editable: false, reason: 'has-bids', hint: MOVE_LOCK_HINT };
  }
  return { editable: true };
}

/** The inputs {@link evaluateLotFieldEditability} reads — raw classifications, never verdicts. */
export interface LotFieldEditabilityInput {
  /** The lot is hammered — SOLD_LOCKED_FIELDS are locked, absolutely (never overridable). */
  isSold: boolean;
  /** Bidding opened and the lot is not terminal (see {@link deriveLotEditLockState}). */
  isActive: boolean;
  /**
   * The lot has at least one PLACED bid — accepted or outbid, never withdrawn
   * (`bids_placed_count_denormalized > 0`; the signed map's Q4). Required for the
   * live bid-ladder lock to bite. The `auctionId` delegation below reads it too;
   * the server move gate, which must also count withdrawn bid rows, calls
   * {@link evaluateLotMoveVerdict} directly with its own any-bid read.
   */
  hasBids: boolean;
  /**
   * The caller holds AUCTIONS_MANAGE, which clears the live bid-ladder lock on ANY
   * field — with a caution that the lot has bids (BL-1908). It does NOT clear the
   * sold lock, the `pricingType` basis lock (BL-2352) NOR the `auctionId` move gate.
   */
  hasAuctionsManage: boolean;
  /** BL-1070 — the lot is in the movable window (draft/ready). Required for `auctionId` to be editable. */
  isDraftOrReady: boolean;
  /** BL-1070 — the lot has ≥1 active `max_bids` (a standing pre-bid). Blocks the move; names the pre-bid in the Starting Price caution. */
  hasActivePreBids: boolean;
  /**
   * BL-1908 — the lot's AUCTION-AWARE derived phase kind (the lot-detail DTO's
   * `phaseKind`; `deriveBatchPhase(row).kind` in bulk). Drives the
   * bidder-visibility cautions only — it never changes a lock. `null` = none
   * shown: the write gates pass `null` because they enforce locks and have no
   * caution to show, and a non-terminal Live Webcast lot has no derivable phase.
   */
  phaseKind: LotPhaseKind | null;
  /**
   * BL-2318 — the caller's edit scope ({@link resolveLotEditScope}). Required, so a
   * write path that forgets it fails to compile rather than widening access:
   * `'accounting'` locks every governed field outside
   * {@link ACCOUNTING_LOT_EDIT_FIELDS} with `role-scope`. Paths whose caller always
   * holds the full lot-edit grant (bulk Edit lots, reserve, reorder, the clerk
   * console) pass `'all'`.
   */
  editScope: LotEditScope;
}

/**
 * Evaluate per-field editability for a lot in its current lock state.
 *
 * @param input See {@link LotFieldEditabilityInput}.
 * @returns A verdict for every governed {@link LotGovernedFieldKey} and every
 *   caution-only {@link LotCautionFieldKey}. Fields not in the map (pure
 *   catalog) are always editable.
 * @sideEffect none — pure.
 */
export function evaluateLotFieldEditability(input: LotFieldEditabilityInput): LotFieldVerdicts {
  const locks = evaluateLotLocks(input);
  const visible = input.phaseKind != null && BIDDER_VISIBLE_PHASES.has(input.phaseKind);
  const bidderCaution = (warn: string): CautionFieldVerdict => (visible ? { editable: true, warn } : { editable: true });

  return {
    ...locks,
    // A plain-editable price or ring gets the visibility caution; a locked or
    // overridden one already says what matters more (locked / has bids). Both
    // cautions differ by stage: pre-bidding is still fluid, a live sale is frozen.
    startPrice: withVisibilityCaution(locks.startPrice, visible, startPriceCaution(input)),
    ringId: withVisibilityCaution(
      locks.ringId,
      visible,
      input.phaseKind === 'pre-bidding' ? LOT_RING_PRE_BIDDING_WARN : LOT_RING_LIVE_WARN,
    ),
    categoryId: bidderCaution(LOT_BIDDERS_SEE_IT_WARN),
    isTitledItem: bidderCaution(LOT_BIDDERS_SEE_IT_WARN),
    // No sold-lot caution here, although the map has one: it rested on the buyer's
    // invoice, and the rule book carries no invoice rules (see the header).
    locationId: bidderCaution(LOT_BIDDERS_SEE_IT_WARN),
  };
}

/**
 * The lock layer: sold and live bid-ladder locks per governed field (iterating
 * {@link GOVERNED_LOCK_FIELDS}, never a single lock set — see BL-1308), the
 * BL-2352 pricing-basis lock, the Admin override as a caution, and the
 * `auctionId` move verdict.
 *
 * @param input See {@link LotFieldEditabilityInput}; `phaseKind` is not read here.
 * @returns A verdict for every governed field.
 * @sideEffect none — pure.
 */
function evaluateLotLocks(input: LotFieldEditabilityInput): Record<LotGovernedFieldKey, FieldVerdict> {
  const { isSold, isActive, hasBids, hasAuctionsManage, isDraftOrReady, hasActivePreBids } = input;
  // The live bid-ladder lock bites on an opened, non-terminal lot with a placed bid.
  const activeBidBearing = isActive && hasBids;
  const activeSet = ACTIVE_LOCKED_FIELDS as readonly string[];
  // BL-1308: the sold set is a membership test now, not the loop. See
  // GOVERNED_LOCK_FIELDS for why iterating it instead fails silently.
  const soldSet = SOLD_LOCKED_FIELDS as readonly string[];

  const verdicts = {} as Record<LotGovernedFieldKey, FieldVerdict>;
  for (const field of GOVERNED_LOCK_FIELDS) {
    if (isSold && soldSet.includes(field)) {
      // Sold outranks active and is never overridable (a sold lot is terminal).
      // BL-1308: guarded on sold-set MEMBERSHIP now, not on `isSold` alone. A
      // sold-editable member of the union (`reserve` / `increment`) falls through
      // to the active branch, which is inert on a genuinely sold lot — sold is
      // terminal, so `deriveLotEditLockState` reports `isActive: false`.
      verdicts[field] = {
        editable: false,
        reason: 'sold-lock',
        hint: SOLD_LOCK_HINT_BY_FIELD[field] ?? SOLD_LOCK_HINT,
      };
    } else if (!isLotFieldInEditScope(field, input.editScope)) {
      // BL-2318: the role can't edit this field in any phase. After the sold
      // branch, so a sold lot's tax keeps its "change it on the invoice" hint.
      verdicts[field] = { editable: false, reason: 'role-scope', hint: ROLE_SCOPE_HINT };
    } else if (field === 'pricingType' && (hasBids || hasActivePreBids)) {
      // BL-2352 (rule #23 consent: Michael Lawrie, 2026-10-08): once the lot has
      // a placed bid or a standing pre-bid, its basis is locked in every unsold
      // phase, with no Admin override. A flip re-prices every bid on the lot
      // (ADR 035 — Money Units, Decision 1). Acreage keeps the override below.
      verdicts[field] = { editable: false, reason: 'has-bids', hint: PRICING_BASIS_LOCK_HINT };
    } else if (activeBidBearing && activeSet.includes(field)) {
      // AUCTIONS_MANAGE clears any live bid-ladder lock — the map's "an Admin can
      // override" cells — and the Admin is told why it matters (BL-1908, Q2).
      verdicts[field] = hasAuctionsManage
        ? { editable: true, warn: OVERRIDE_WARN_BY_FIELD[field] ?? LOT_ACTIVE_OVERRIDE_WARN }
        : { editable: false, reason: 'active-lock', hint: ACTIVE_LOCK_HINT };
    } else {
      verdicts[field] = { editable: true };
    }
  }

  // BL-1070 — auctionId (move/assign/reassign). BL-1073 extracted the rule body
  // to `evaluateLotMoveVerdict` so the bulk `move-to-auction` batch action shares
  // it verbatim; this is a delegation, never a second copy.
  verdicts.auctionId = isLotFieldInEditScope('auctionId', input.editScope)
    ? evaluateLotMoveVerdict({ isDraftOrReady, hasBids, hasActivePreBids })
    : { editable: false, reason: 'role-scope', hint: ROLE_SCOPE_HINT };

  return verdicts;
}

/**
 * The Starting Price caution for a lot bidders can see and nobody has bid on: on a
 * pre-bidding lot it names any standing pre-bid, on a live lot it is the minimum
 * first bid.
 *
 * @param input See {@link LotFieldEditabilityInput}.
 * @returns The caution text.
 * @sideEffect none — pure.
 */
function startPriceCaution(input: LotFieldEditabilityInput): string {
  if (input.phaseKind !== 'pre-bidding') return LOT_LIVE_START_PRICE_WARN;
  return input.hasActivePreBids ? LOT_PRE_BID_START_PRICE_WARN : LOT_BIDDERS_SEE_IT_WARN;
}

/**
 * Add the bidder-visibility caution to a governed field's verdict when it is
 * plainly editable. A lock, or a verdict that already carries a caution (the
 * Admin override), is returned unchanged.
 *
 * @param verdict The lock layer's verdict for the field.
 * @param visible Whether bidders can see the lot right now.
 * @param warn The caution to attach.
 * @returns The verdict to publish.
 * @sideEffect none — pure.
 */
function withVisibilityCaution(verdict: FieldVerdict, visible: boolean, warn: string): FieldVerdict {
  if (!visible || verdict.editable === false || 'warn' in verdict) return verdict;
  return { editable: true, warn };
}

/** A field the lot rule book answers for — governed (lockable) or caution-only. */
export type LotVerdictFieldKey = LotGovernedFieldKey | LotCautionFieldKey;

/** A refused field and its lock verdict. */
export interface LotFieldRefusal {
  field: LotVerdictFieldKey;
  verdict: Extract<FieldVerdict, { editable: false }>;
}

/** What the rule book says about one lot's requested changes — see {@link resolveLotFieldEdit}. */
export interface LotFieldEditResolution {
  /** Changed fields the rule book refuses, sold locks first (they are never overridable). */
  refused: LotFieldRefusal[];
  /** Changed fields whose live bid-ladder lock the caller's AUCTIONS_MANAGE cleared (audited as an override). */
  overridden: LotVerdictFieldKey[];
  /** Changed fields that save with a caution (the signed map's ⚠ cells and the Admin override). */
  cautioned: Array<{ field: LotVerdictFieldKey; warn: string }>;
}

/**
 * BL-1910 (M4) — the ONE decision over a lot's requested field changes, shared by
 * the lot panel's gate (`LotEditGateService`) and Edit lots
 * (`BatchLotFieldEditService`), so single and bulk give the same answer for the
 * same field on the same lot and user.
 *
 * Runs {@link evaluateLotFieldEditability} permission-aware for the decision, and
 * once more without AUCTIONS_MANAGE to tell an override apart from a plain edit
 * (the lot panel's `bid_ladder_overridden` audit verb, BL-856).
 *
 * @param input.lock The lock-state inputs read under the row lock (and `phaseKind` for cautions).
 * @param input.changedFields The fields the caller is changing.
 * @returns Refusals, overrides and cautions for the changed fields only.
 * @sideEffect none — pure.
 */
export function resolveLotFieldEdit(input: {
  lock: LotFieldEditabilityInput;
  changedFields: readonly LotVerdictFieldKey[];
}): LotFieldEditResolution {
  const { lock, changedFields } = input;
  const verdicts = evaluateLotFieldEditability(lock);
  const baseVerdicts = lock.hasAuctionsManage
    ? evaluateLotFieldEditability({ ...lock, hasAuctionsManage: false })
    : verdicts;

  const refused: LotFieldRefusal[] = [];
  const overridden: LotVerdictFieldKey[] = [];
  const cautioned: Array<{ field: LotVerdictFieldKey; warn: string }> = [];
  for (const field of changedFields) {
    const verdict: FieldVerdict = verdicts[field];
    const locked = fieldLock(verdict);
    if (locked) {
      refused.push({ field, verdict: locked });
      continue;
    }
    const base = fieldLock(baseVerdicts[field]);
    if (base?.reason === 'active-lock') overridden.push(field);
    const warn = fieldCaution(verdict);
    if (warn !== undefined) cautioned.push({ field, warn });
  }
  // Sold locks outrank live locks: a sold lot is terminal and never overridable.
  refused.sort((a, b) => Number(b.verdict.reason === 'sold-lock') - Number(a.verdict.reason === 'sold-lock'));
  return { refused, overridden, cautioned };
}
